/**
 * OrgSettingsPage — admin-only switchboard for tenant-level capability
 * toggles. Each row is one contract-driven workflow; the switch flips
 * the corresponding key in `tenant_settings.feature_flags`.
 *
 * Director directive (2026-05-11): every modular workflow must have a
 * visible switch. This page is that switch surface; the read side
 * (`useTenantFeatures`) consumes the same column.
 *
 * Visible to: contractor_admin, super_admin (RLS enforces; the route
 * wrapper also gates). Other roles do not see the sidebar link and
 * are redirected from the path.
 *
 * Audit trail: every flip writes to `audit_logs` as
 * `tenant.feature_flags_changed` via the AFTER UPDATE trigger
 * `trg_tenant_feature_flags_audit` on the tenant_settings table.
 */

import { useState, type ReactNode }      from 'react'
import { useTranslation }                 from 'react-i18next'
import { useTenantFeatures, KNOWN_FLAGS, type TenantFeatureFlag } from '@/hooks/useTenantFeatures'
import { useUpdateTenantFeatureFlag }    from '@/hooks/useUpdateTenantFeatureFlag'

interface FeatureDescriptor {
  flag:        TenantFeatureFlag
  label:       string
  description: string
}

// Adding a flag: add an entry here and in KNOWN_FLAGS (the hook).
// The page renders only flags listed in FLAGS so an unlabelled flag
// can never appear half-described in the UI.
const FLAGS: readonly FeatureDescriptor[] = [
  {
    flag:        'additional_fee_workflow',
    label:       'Additional-fee requests',
    description: 'Lets cleaners file exceedance fee requests during a clean and routes them to an owner-approver for decision. Pairs with the tier-overage tripwire on task completion. Turn off if your contract does not include an exceedance-fee clause.',
  },
  {
    flag:        'abandoned_belongings_tracking',
    label:       'Abandoned-belongings reports',
    description: 'Lets cleaners flag abandoned belongings during a clean with a photo + description; supervisor acknowledges and records disposition. Turn off if your contract does not require this record-keeping.',
  },
] as const

export function OrgSettingsPage(): ReactNode {
  const { t } = useTranslation()
  const { has, isLoading } = useTenantFeatures()
  const update = useUpdateTenantFeatureFlag()
  const [pendingFlag, setPendingFlag] = useState<TenantFeatureFlag | null>(null)

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('orgSettings.title', 'Organization settings')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
          {t('orgSettings.subtitle', 'Per-workflow toggles for your organization. Changes log to audit_logs as tenant.feature_flags_changed.')}
        </p>
      </header>

      <ul
        data-testid="org-settings-flag-list"
        style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}
      >
        {FLAGS.map((f) => {
          const enabled = has(f.flag)
          const busy    = pendingFlag === f.flag && update.isPending
          return (
            <li
              key={f.flag}
              data-testid={`org-settings-flag-${f.flag}`}
              data-flag={f.flag}
              data-enabled={enabled}
              style={{
                background:    'var(--brand-white)',
                border:        '1px solid var(--brand-neutral-300)',
                borderRadius:  'var(--brand-radius-lg)',
                padding:       '0.85rem 1rem',
                display:       'flex',
                gap:           '1rem',
                alignItems:    'center',
                justifyContent:'space-between',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '1rem' }}>{f.label}</div>
                <div style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem', marginTop: '0.2rem' }}>
                  {f.description}
                </div>
              </div>
              <label
                data-testid={`org-settings-toggle-${f.flag}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: busy ? 'wait' : 'pointer' }}
              >
                <input
                  type="checkbox"
                  role="switch"
                  checked={enabled}
                  disabled={isLoading || busy}
                  onChange={(e) => {
                    setPendingFlag(f.flag)
                    update.mutate(
                      { flag: f.flag, enabled: e.target.checked },
                      { onSettled: () => setPendingFlag(null) },
                    )
                  }}
                  style={{ width: '2.5rem', height: '1.4rem', cursor: busy ? 'wait' : 'pointer' }}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
                  {enabled ? t('orgSettings.on', 'On') : t('orgSettings.off', 'Off')}
                </span>
              </label>
            </li>
          )
        })}
      </ul>

      {update.error && (
        <p
          role="alert"
          data-testid="org-settings-error"
          style={{
            color:       'var(--brand-danger)',
            background:  'var(--brand-danger-subtle)',
            border:      '1px solid var(--brand-danger)',
            borderRadius:'var(--brand-radius-md)',
            padding:     '0.6rem 0.85rem',
            fontSize:    '0.9rem',
            margin:       0,
          }}
        >
          {update.error instanceof Error ? update.error.message : t('orgSettings.failedToSave', 'Failed to save the setting.')}
        </p>
      )}

      <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem', margin: 0 }}>
        {t(
          'orgSettings.knownFlagCount',
          'Known flags: {{count}}. To add another contract-driven workflow toggle, extend KNOWN_FLAGS in useTenantFeatures and the FLAGS array on this page.',
          { count: Object.keys(KNOWN_FLAGS).length },
        )}
      </p>
    </section>
  )
}
